import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Input,
  Layout,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CalendarOutlined,
  CopyOutlined,
  ReloadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useAuthenticatedFetch } from "@/Api/Axios";

const { Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

const COLORS = ["#1890ff", "#52c41a", "#faad14", "#eb2f96", "#722ed1", "#13c2c2", "#fa541c"];
const getAvatarColor = (name) => {
  let hash = 0;
  for (let i = 0; i < (name || "").length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return COLORS[Math.abs(hash) % COLORS.length];
};
const getInitials = (name) => {
  if (!name || name === "Unknown") return "?";
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
};

const Reviews = () => {
  const fetch = useAuthenticatedFetch();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState({
    createdDate: "",
    sortOrder: "latest",
  });
  const [selectedReview, setSelectedReview] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  const fetchReviews = useCallback(
    async (page = currentPage, size = pageSize, search = searchTerm, filterParams = filters) => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          page: page.toString(),
          pageSize: size.toString(),
        });

        if (search && search.trim()) queryParams.append("search", search.trim());
        if (filterParams.createdDate) queryParams.append("createdDate", filterParams.createdDate);
        if (filterParams.sortOrder) queryParams.append("sortOrder", filterParams.sortOrder);

        const response = await fetch.get(`admin/reviews?${queryParams}`, false);
        if (response?.data) {
          setReviews(response.data.reviews || []);
          setTotalCount(response.data.pagination?.total || 0);
          setTotalPages(response.data.pagination?.totalPages || 0);
          setCurrentPage(response.data.pagination?.current || 1);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
        setReviews([]);
        setTotalCount(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchReviews();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
    fetchReviews(1, pageSize, searchTerm, filters);
  }, [pageSize]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const debouncedSearch = useCallback(
    (value) => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        setSearchTerm(value);
        setCurrentPage(1);
        fetchReviews(1, pageSize, value, filters);
      }, 500);
    },
    [pageSize, filters]
  );

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleSearch = (value) => {
    debouncedSearch(value);
  };

  const handleSearchClear = () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    fetchReviews(1, pageSize, "", filters);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
    setCurrentPage(1);
    fetchReviews(1, pageSize, searchTerm, newFilters);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchReviews(page, pageSize, searchTerm, filters);
  };

  const handlePrevious = () => {
    if (currentPage > 1) handlePageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) handlePageChange(currentPage + 1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 10;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 6) {
      for (let i = 1; i <= maxVisible; i++) pages.push(i);
    } else if (currentPage >= totalPages - 5) {
      for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = currentPage - 5; i <= currentPage + 4; i++) pages.push(i);
    }
    return pages;
  };

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);

  const hasActiveFilters = filters.createdDate || (filters.sortOrder && filters.sortOrder !== "latest");

  const defaultFilters = { createdDate: "", sortOrder: "latest" };

  const columns = [
    {
      title: "#",
      dataIndex: "_id",
      key: "no",
      width: 50,
      align: "center",
      render: (_, _record, index) => (
        <Text type="secondary" style={{ fontSize: "13px" }}>
          {startIndex + index + 1}
        </Text>
      ),
    },
    {
      title: "User Info",
      key: "userInfo",
      width: 280,
      render: (_, record) => (
        <div style={{ padding: "4px 0" }}>
          <Space size="small" style={{ marginBottom: 6 }}>
            <Avatar
              size="small"
              style={{ backgroundColor: getAvatarColor(record.userName), fontSize: 11, fontWeight: 600 }}
            >
              {getInitials(record.userName)}
            </Avatar>
            <Text strong>{record.userName}</Text>
          </Space>

          {record.userEmail && (
            <div>
              <Text style={{ color: "#000", fontWeight: "500" }}>Email: </Text>
              <Text style={{ color: "#000" }}>{record.userEmail}</Text>
            </div>
          )}

          <div>
            <Text style={{ color: "#000", fontWeight: "500" }}>User ID: </Text>
            <Text style={{ color: "#1890ff", fontSize: "11px", fontFamily: "monospace" }}>{record.userId}</Text>
            {record.userId && (
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(record.userId);
                }}
                style={{ marginLeft: "4px", padding: "0 4px" }}
              />
            )}
          </div>

        </div>
      ),
    },
    {
      title: "Review",
      key: "review",
      render: (_, record) => {
        const text = record.review || "";
        const maxChars = 200;
        const isLong = text.length > maxChars;
        const displayText = isLong ? text.slice(0, maxChars) + "..." : text;
        return (
          <div style={{ padding: "4px 0" }}>
            <Text style={{ fontSize: 13, color: "#595959" }}>
              {displayText}
            </Text>
            {isLong && (
              <>
                {" "}
                <Text
                  style={{
                    fontSize: 12,
                    color: "#1890ff",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedReview(record);
                    setModalVisible(true);
                  }}
                >
                  Read more
                </Text>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: "Submitted",
      key: "submittedAt",
      width: 160,
      render: (_, record) => {
        if (!record.submittedAt) return <Text type="secondary">-</Text>;
        const date = new Date(record.submittedAt);
        const datePart = date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        const timePart = date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
        return (
          <div style={{ padding: "4px 0" }}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <CalendarOutlined style={{ marginRight: 4 }} />
                {datePart}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {timePart}
              </Text>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <Layout.Content style={{ padding: "24px", width: "100%", overflow: "hidden" }}>
      <style>{`
        .review-search .ant-input::placeholder,
        .review-search .ant-input-affix-wrapper input::placeholder {
          color: #666666 !important;
          opacity: 1 !important;
        }
        .review-filter .ant-select-selector .ant-select-selection-placeholder {
          color: #666666 !important;
          opacity: 1 !important;
        }
      `}</style>

      <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden", marginTop: "12px" }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* Header with Search + Refresh + Count */}
          <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" }}>
            <div style={{ flex: 1, maxWidth: "800px" }}>
              <Search
                className="review-search"
                placeholder="Search by name, email, or review text..."
                allowClear
                enterButton="Search"
                size="large"
                value={searchInput}
                onSearch={handleSearch}
                onChange={handleSearchInputChange}
                onClear={handleSearchClear}
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <Tooltip title="Refresh">
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  size="large"
                  loading={loading}
                  onClick={() => fetchReviews(currentPage, pageSize, searchTerm, filters)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  Refresh
                </Button>
              </Tooltip>

              <div
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "6px",
                  border: "1px solid #d9d9d9",
                  minWidth: "200px",
                  textAlign: "center",
                }}
              >
                <Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
                  {hasActiveFilters ? "Filtered: " : "Total: "}
                  {totalCount.toLocaleString()}
                </Text>
                <Text style={{ fontSize: "12px", color: "#666", marginLeft: "4px" }}>
                  review{totalCount === 1 ? "" : "s"}
                </Text>
              </div>
            </div>
          </div>

          {/* Filter Row - smaller width */}
          <div style={{ display: "flex", gap: "12px", marginBottom: "8px", maxWidth: "400px" }}>
            <div style={{ width: "200px" }}>
              <Select
                className="review-filter"
                placeholder="Created Date"
                allowClear
                size="small"
                style={{ width: "100%" }}
                value={filters.createdDate || undefined}
                onChange={(value) => handleFilterChange("createdDate", value || "")}
              >
                <Option value="today">Today</Option>
                <Option value="yesterday">Yesterday</Option>
                <Option value="last_3_days">Last 3 Days</Option>
                <Option value="last_7_days">Last 7 Days</Option>
                <Option value="last_15_days">Last 15 Days</Option>
                <Option value="last_30_days">Last 30 Days</Option>
                <Option value="last_60_days">Last 60 Days</Option>
                <Option value="last_90_days">Last 90 Days</Option>
                <Option value="last_6_months">Last 6 Months</Option>
                <Option value="last_year">Last Year</Option>
                <Option value="week">This Week</Option>
                <Option value="month">This Month</Option>
                <Option value="year">This Year</Option>
              </Select>
            </div>
            <div style={{ width: "160px" }}>
              <Select
                className="review-filter"
                placeholder="Sort Order"
                allowClear
                size="small"
                style={{ width: "100%" }}
                value={filters.sortOrder || undefined}
                onChange={(value) => handleFilterChange("sortOrder", value || "latest")}
              >
                <Option value="latest">Latest First</Option>
                <Option value="oldest">Oldest First</Option>
              </Select>
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div
              style={{
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "6px",
                border: "1px solid #d9d9d9",
                marginBottom: "8px",
              }}
            >
              <Text strong style={{ marginRight: "8px", color: "#666" }}>Active Filters:</Text>
              {filters.createdDate && (
                <Tag closable onClose={() => handleFilterChange("createdDate", "")} color="purple" style={{ marginRight: "4px" }}>
                  Date: {filters.createdDate.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                </Tag>
              )}
              {filters.sortOrder && filters.sortOrder !== "latest" && (
                <Tag closable onClose={() => handleFilterChange("sortOrder", "latest")} color="cyan" style={{ marginRight: "4px" }}>
                  Sort: Oldest First
                </Tag>
              )}
              <Button
                type="text"
                size="small"
                style={{ marginLeft: "8px", color: "#666" }}
                onClick={() => {
                  setFilters(defaultFilters);
                  setCurrentPage(1);
                  fetchReviews(1, pageSize, searchTerm, defaultFilters);
                }}
              >
                Clear All Filters
              </Button>
            </div>
          )}

          {/* Data Table */}
          <Table
            columns={columns}
            dataSource={reviews}
            loading={loading}
            rowKey="_id"
            pagination={false}
            size="middle"
            onRow={(record) => ({
              onClick: () => {
                setSelectedReview(record);
                setModalVisible(true);
              },
              style: { cursor: "pointer" },
            })}
          />

          {/* Page Size Selector */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "16px", gap: "8px" }}>
            <Text style={{ fontSize: "14px", color: "#666" }}>Items per page:</Text>
            <Select
              value={pageSize}
              onChange={(value) => {
                setPageSize(value);
                setCurrentPage(1);
                fetchReviews(1, value, searchTerm, filters);
              }}
              style={{ width: "80px" }}
              size="small"
            >
              <Option value={10}>10</Option>
              <Option value={25}>25</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
            </Select>
          </div>

          {/* Custom Pagination - same as User page */}
          {totalCount > 0 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "16px", gap: "8px" }}>
              <Button
                disabled={currentPage === 1}
                onClick={handlePrevious}
                style={{
                  minWidth: "80px", height: "32px", borderRadius: "6px", fontSize: "14px", fontWeight: "500",
                  border: "1px solid #d9d9d9",
                  backgroundColor: currentPage === 1 ? "#f5f5f5" : "#fff",
                  color: currentPage === 1 ? "#d9d9d9" : "#1890ff",
                  marginRight: "8px",
                }}
              >
                Previous
              </Button>

              {totalPages > 1 &&
                getPageNumbers().map((pageNum) => (
                  <Button
                    key={pageNum}
                    type={currentPage === pageNum ? "primary" : "default"}
                    onClick={() => handlePageChange(pageNum)}
                    style={{
                      minWidth: "32px", height: "32px", borderRadius: "6px", fontSize: "14px", fontWeight: "500",
                      border: currentPage === pageNum ? "none" : "1px solid #d9d9d9",
                      backgroundColor: currentPage === pageNum ? "#1890ff" : "transparent",
                      color: currentPage === pageNum ? "#fff" : "#262626",
                    }}
                  >
                    {pageNum}
                  </Button>
                ))}

              {totalPages === 1 && (
                <span style={{ fontSize: "14px", color: "#666", fontWeight: "500", margin: "0 16px" }}>
                  Page 1 of 1
                </span>
              )}

              <Button
                disabled={currentPage === totalPages}
                onClick={handleNext}
                style={{
                  minWidth: "80px", height: "32px", borderRadius: "6px", fontSize: "14px", fontWeight: "500",
                  border: "1px solid #d9d9d9",
                  backgroundColor: currentPage === totalPages ? "#f5f5f5" : "#fff",
                  color: currentPage === totalPages ? "#d9d9d9" : "#1890ff",
                  marginLeft: "8px",
                }}
              >
                Next
              </Button>
            </div>
          )}

          {/* Count info */}
          {totalCount > 0 && (
            <div style={{ textAlign: "center", marginTop: "16px", color: "#666", fontSize: "14px" }}>
              Showing {Math.min(startIndex + 1, totalCount)}-{endIndex} of {totalCount} review
              {totalCount === 1 ? "" : "s"}
              {totalCount > 0 && (
                <span style={{ marginLeft: "16px", color: "#999" }}>
                  Page {currentPage} of {totalPages}
                </span>
              )}
            </div>
          )}
        </Space>
      </div>

      {/* Review Detail Modal */}
      <Modal
        title={null}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={560}
        styles={{ body: { padding: 0 } }}
      >
        {selectedReview && (
          <div>
            {/* Modal Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #f0f0f0",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <Avatar
                size={44}
                style={{
                  backgroundColor: getAvatarColor(selectedReview.userName),
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                {getInitials(selectedReview.userName)}
              </Avatar>
              <div>
                <Text strong style={{ fontSize: 16, display: "block" }}>{selectedReview.userName}</Text>
                {selectedReview.userEmail && (
                  <Text type="secondary" style={{ fontSize: 13 }}>{selectedReview.userEmail}</Text>
                )}
              </div>
            </div>

            {/* Review Content */}
            <div style={{ padding: "20px 24px" }}>
              <Paragraph
                style={{
                  fontSize: 15,
                  lineHeight: 1.7,
                  color: "#262626",
                  whiteSpace: "pre-wrap",
                  marginBottom: 16,
                }}
              >
                {selectedReview.review}
              </Paragraph>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: 12,
                  borderTop: "1px solid #f5f5f5",
                }}
              >
                {selectedReview.userId && (
                  <div>
                    <Text style={{ fontSize: 11, color: "#999" }}>User ID: </Text>
                    <Text
                      copyable={{ icon: <CopyOutlined style={{ fontSize: 11 }} /> }}
                      code
                      style={{ fontSize: 11 }}
                    >
                      {selectedReview.userId}
                    </Text>
                  </div>
                )}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  {formatDate(selectedReview.submittedAt)}
                </Text>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </Layout.Content>
  );
};

export default Reviews;
